import React, { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import { useGridUserDataGlobalFiltersContext } from "../../../contexts/GridUserDataGlobalFiltersProvider";
import { useTerminalContext } from "../contexts/TerminalContext";
import { useConfigFileList } from '../hooks/useConfigFileList';
import { useReadyConfigHistory } from "../hooks/useReadyConfigHistory";
import type { ViewState } from "../shared/types";
import { ConfigFileManager } from "./ConfigFileManager";
import { ConfigStatusBadge } from "./ConfigStatusBadge";
import { EditControls } from "./EditControls";
import { EmptyState } from "./EmptyState";
import { ErrorMessage } from "./ErrorMessage";
import { GPSEditor } from "./GPSEditor";
import { InfoBox } from "./InfoBox";
import { RowDirectionControl } from "./RowDirectionControl";
import { SavePublishControls } from "./SavePublishControls";
import { SidePanel } from "./SidePanel";
import { UploadOldAddressMapping } from './UploadOldAddressMapping';
import { VisibilityControls } from "./VisibilityControls";

interface EditorTabProps {
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
}

export const EditorTab: React.FC<EditorTabProps> = ({ viewState, setViewState }) => {
    const notify = useNotify();
    const translate = useTranslate();
    const { readyConfig, error, fileList, configFileStatus, currentConfigSrc } = useTerminalContext();
    const { data: globalTerminalId = {} } = useGridUserDataGlobalFiltersContext();
    const { listConfigFiles } = useConfigFileList();
    const { updateReadyConfig, undo, redo, canUndo, canRedo } = useReadyConfigHistory();

    const [showAreas, setShowAreas] = useState(true);
    const [showRows, setShowRows] = useState(true);
    const [showPorts, setShowPorts] = useState(true);
    const [selectedSrc, setSelectedSrc] = useState<string>('');
    const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
    const terminalId = globalTerminalId?.terminal_id?.[0];

    // Load config files when terminal changes
    useEffect(() => {
      if (terminalId) {
        listConfigFiles(terminalId).catch(err => {
          notify(err.message, 'error');
        });
      }
    }, [terminalId, listConfigFiles, notify]);

    // Set selected src when fileList or readyConfig changes
    useEffect(() => {
      if (fileList.length === 0) return;
      if (currentConfigSrc) {
        setSelectedSrc(currentConfigSrc);
        return;
      }
      if (!selectedSrc) {
        const currentFile = fileList.find(f => f.status === configFileStatus);
        setSelectedSrc(currentFile?.src || fileList[0].src);
      }
    }, [fileList, selectedSrc, configFileStatus, currentConfigSrc]);

    const handleReverseRowDirection = () => {
      if (selectedRowIndex === null || !readyConfig) return;

      const newConfig = JSON.parse(JSON.stringify(readyConfig));
      const rowPolygon = newConfig.rowBoundaries[selectedRowIndex].polygon;

      rowPolygon.reverse();

      updateReadyConfig(newConfig);
    };

    return (
      <div style={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          {readyConfig && (
            <GPSEditor
              readyConfig={readyConfig}
              onConfigUpdate={updateReadyConfig}
              viewState={viewState}
              setViewState={setViewState}
              showAreasPoints={showAreas}
              showRowsPoints={showRows}
              showPortsPoints={showPorts}
              selectedRowIndex={selectedRowIndex}
              onSelectedRowChange={setSelectedRowIndex}
            />
          )}
          {!readyConfig && <EmptyState hasTerminal={!!terminalId} />}
        </div>

        <SidePanel>
          {error && error !== "NO_CONFIG_FOUND" && <ErrorMessage message={error} />}
          {(() => {
            const fallbackStatus = fileList.find(f => f.src === selectedSrc)?.status ?? null;
            const statusToShow = configFileStatus !== null ? configFileStatus : fallbackStatus;
            return statusToShow !== null ? <ConfigStatusBadge status={statusToShow} /> : null;
          })()}
          <ConfigFileManager
            terminalId={terminalId}
            fileList={fileList}
            selectedSrc={selectedSrc}
            onSrcChange={setSelectedSrc}
          />
          {readyConfig && (
            <>
              <SavePublishControls
                terminalId={terminalId}
                selectedSrc={selectedSrc}
              />
              <EditControls
                onUndo={undo}
                onRedo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
              />
              <RowDirectionControl
                selectedRowIndex={selectedRowIndex}
                selectedRowName={
                  selectedRowIndex !== null && readyConfig.rowBoundaries[selectedRowIndex]
                    ? readyConfig.rowBoundaries[selectedRowIndex].name
                    : null
                }
                onReverseDirection={handleReverseRowDirection}
              />
              <InfoBox title={translate("osfc.storage_ui.visibility.title")} style={{ marginBottom: "15px" }}>
                <VisibilityControls
                  showAreas={showAreas}
                  setShowAreas={setShowAreas}
                  showRows={showRows}
                  setShowRows={setShowRows}
                  showPorts={showPorts}
                  setShowPorts={setShowPorts}
                />
              </InfoBox>
            </>
          )}
          {terminalId && <UploadOldAddressMapping terminalId={terminalId} />}
        </SidePanel>
      </div>
    );
};
